import React, { useState } from 'react';
import { PageSteps } from '@common/components';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import {
  AddUnitsDetailsStepFormValuesType,
  AddUnitsInsertDetailsStep,
  AddUnitsSelectFacilityStep,
} from '@institutions/components';
import { Form } from 'antd';
import { useMutation, useNavigate } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  OrganizationUnitsCreationMutation,
  OrganizationUnitsCreationMutationResponse,
  OrganizationUnitsCreationMutationVariables,
} from '@institutions/graphql';

export function AdminOrganizationAddUnitsPage() {
  const [unitsForm] = Form.useForm();
  const navigate = useNavigate();
  const [selectedFacility, setSelectedFacility] = useState<
    string | undefined
  >();

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: 'Locations',
      path: ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE,
    },
    {
      name: 'Add New Units',
      path: `${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/add-units`,
    },
  ]);

  const errorToast = () =>
    createMessage('error', 'Something went wrong. Try again later.');

  const [save, { loading: mutationLoading }] = useMutation<
    OrganizationUnitsCreationMutationResponse,
    OrganizationUnitsCreationMutationVariables
  >(OrganizationUnitsCreationMutation, {
    onCompleted(response) {
      if (!response?.createOrganizationUnits) {
        errorToast();
        return;
      }

      const newOrganizationUnitsIds =
        response.createOrganizationUnits.organizationUnits?.map(
          (u: { id: string }) => u.id
        );

      createMessage('success', 'Unit(s) successfully saved.');
      navigate(ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE, {
        state: {
          organizationBusinessId: selectedFacility,
          organizationUnitsIds: newOrganizationUnitsIds,
        },
      });
    },
    onError: errorToast,
  });

  function handleSave(values: AddUnitsDetailsStepFormValuesType) {
    const parsedValues = values.units.map((unit) => ({
      name: unit.unitName.trim(),
      costCenter: unit.costCenter || '',
      clinicalUnitId: unit.clinicalUnitId,
      organizationBusinessId: selectedFacility,
    }));

    save({ variables: { input: parsedValues } });
  }

  return (
    <PageSteps
      onCancel={() => navigate(ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE)}
      onConfirm={unitsForm.submit}
      confirmLoading={mutationLoading}
      steps={[
        {
          title: 'Select Facility',
          page: (
            <AddUnitsSelectFacilityStep
              selectedFacilityId={selectedFacility}
              onChange={setSelectedFacility}
            />
          ),
          forwardButtonDisabled: !selectedFacility,
          buttonsAlignment: 'left',
        },
        {
          title: 'Unit(s) Details',
          page: (
            <AddUnitsInsertDetailsStep
              unitsForm={unitsForm}
              selectedFacilityId={selectedFacility}
              onFormFinish={handleSave}
            />
          ),
        },
      ]}
    />
  );
}
